import classnames from 'classnames'
import * as React from 'react'
import * as Autocomplete from 'react-autocomplete'

const PoweredByGoogleLogo = require('assets/components/powered_by_google.svg')
const styles = require('./styles')

export interface CommonProps {
  containerClass?: string
  fieldKey?: string
  value: string
  label?: string
  placeholder?: string
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void
  onChange?: (event: React.FormEvent<HTMLInputElement>, value: string) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSelect?: (value: string, item: React.ReactNode) => string | void
  required?: boolean
  styles?: FormStyles
  getItemValue?: (item: React.ReactNode) => string
  renderItem?: (item: React.ReactNode, isHighlighted: boolean) => React.ReactNode
  forceFocus?: boolean
  showGoogleLogo?: boolean
}

export interface Props extends CommonProps {
  items: object[]
  itemId: (item: object) => string
}

export interface FormStyles {
  customInputStyles?: {}
  field: string
  fieldError: string
  fieldFocus: string
  fieldInput: string
  fieldLabel: string
}

const PoweredByGoogle: React.FC = () => (
  <div className={classnames(styles.option, styles.poweredByGoogleWrapper)}>
    <PoweredByGoogleLogo />
  </div>
)

const FormAutocomplete = ({
  containerClass,
  fieldKey,
  getItemValue,
  onBlur: onBlurProp,
  onChange,
  onKeyDown,
  onSelect,
  placeholder,
  value: propsValue,
  forceFocus,
  showGoogleLogo,
  ...props
}: Props) => {
  const [currentValue, setCurrentValue] = React.useState<string>(propsValue)
  const [isFocused, setIsFocused] = React.useState<boolean>(false)

  const onFocus = () => setIsFocused(true)
  const onBlur = (event: React.FormEvent<HTMLInputElement>) => {
    if (onBlurProp !== undefined) {
      onBlurProp(event)
    }
    setIsFocused(false)
  }

  const dedupedItems = props.items.reduce(
    (acc: object[], next: object) =>
      acc.find((item) => props.itemId(item) === props.itemId(next)) ? acc : [...acc, next],
    [],
  )

  React.useEffect(() => setCurrentValue(propsValue), [propsValue])

  const renderItem = (item: React.ReactNode, isHighlighted: boolean): React.ReactNode => {
    const label = getItemValue?.(item)
    return (
      <div key={label} className={classnames(styles.option, { [styles.highlighted]: isHighlighted })}>
        {label}
      </div>
    )
  }

  const renderMenu = (items: React.ReactNode[]): React.ReactNode => {
    const menuItems = showGoogleLogo ? [...items, <PoweredByGoogle key={'google_logo'} />] : items
    return <div className={classnames(styles.menu, { [styles.menuHidden]: items.length === 0 })} children={menuItems} />
  }

  return (
    <div className={classnames(containerClass, props.styles && { [props.styles.fieldFocus]: isFocused })}>
      <div className={props.styles?.fieldLabel}>{props.label}</div>
      <Autocomplete
        getItemValue={getItemValue}
        value={currentValue}
        items={dedupedItems}
        renderItem={renderItem}
        renderMenu={renderMenu}
        onChange={onChange}
        onSelect={onSelect}
        wrapperStyle={{ display: 'block', position: 'relative' }}
        inputProps={{
          className: props.styles?.fieldInput,
          id: fieldKey,
          name: fieldKey,
          onBlur,
          onFocus,
          onKeyDown,
          placeholder,
          style: props.styles?.customInputStyles,
          autoFocus: forceFocus,
        }}
      />
    </div>
  )
}

export default FormAutocomplete
